import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faUser, faCheckCircle, faInfoCircle, faSpinner, faPlus, faLeaf, faSearch } from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import TaskService from '../../../AppSettings/services/TaskService';
import MatterService from '../../../AppSettings/services/MatterService';
import EmployeeService from '../../../AppSettings/services/EmployeeService';
import toastService from '../../../AppSettings/services/ToastService';
import './CreateTasksPage.css';
import useActiveEmployeeId from '../../../AppSettings/hooks/useActiveEmployeeId';
import { isEngineeringEnvironment } from '../../../AppSettings/environmentConfig';
import { AddTaskRequest, AddTaskResponse } from '../../../Models/Tasks/TaskRequestResponseModels';
import { SelectOption } from '../../../Models/Form/FormModels';
import { Matter } from '../../../Models/Matter/MatterModels';
import { Employees } from '../../../Models/Employee/EmployeeModels';
import FormInput from '@frontend/Components/Forms/FormInput/FormInput';
import useFormDraft from '../../../AppSettings/hooks/useFormDraft';

interface TaskFormData extends Omit<AddTaskRequest, 'duedate'> {
  duedate: string;
  priority?: string;
}

interface CreateTasksPageProps {
  onClose?: () => void;
}

const DRAFT_KEY = 'task-draft-global';

const TASK_FORM_DEFAULTS: TaskFormData = {
  matterid: '',
  task_type: '',
  assignedto: '',
  duedate: '',
  description: '',
  task_title: '',
  status: 'Pending',
  priority: 'Medium',
  createdby: '',
};

const CreateTasksPage: React.FC<CreateTasksPageProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const employeeId = useActiveEmployeeId();

  const taskTypes: SelectOption[] = [
    { value: 'Internal', label: 'Internal' },
    { value: 'External', label: 'External' },
  ];

  const priorityOptions: SelectOption[] = [
    { value: 'Low', label: 'Low' },
    { value: 'Medium', label: 'Medium' },
    { value: 'High', label: 'High' },
    { value: 'Urgent', label: 'Urgent' },
  ];

  const statusOptions: SelectOption[] = [
    { value: 'Pending', label: 'Pending' },
    { value: 'In Progress', label: 'In Progress' },
    { value: 'Completed', label: 'Completed' },
  ];

  const [formData, setFormData] = useState<TaskFormData>({
    matterid: '',
    task_type: '',
    assignedto: '',
    duedate: '',
    description: '',
    task_title: '',
    status: 'Pending',
    priority: 'Medium',
    createdby: employeeId || '',
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [matters, setMatters] = useState<SelectOption[]>([]);
  const [employees, setEmployees] = useState<SelectOption[]>([]);
  const [loadingMatters, setLoadingMatters] = useState<boolean>(true);
  const [loadingEmployees, setLoadingEmployees] = useState<boolean>(true);
  const [currentEmployee, setCurrentEmployee] = useState<Employees | null>(null);

  const { draftRestored, dismissDraft, clearDraft } = useFormDraft<TaskFormData>(
    DRAFT_KEY,
    formData,
    TASK_FORM_DEFAULTS,
    (saved) => setFormData((prev) => ({ ...prev, ...saved, createdby: prev.createdby })),
  );

  // Fetch matters (Legal) or projects (ENG) for linking to the action item
  useEffect(() => {
    const fetchMatters = async () => {
      if (!currentEmployee || !employeeId) return;

      try {
        setLoadingMatters(true);
        const permishIdNum = parseInt(currentEmployee.permish_id?.toString() || '0');

        let options: SelectOption[] = [];

        if (permishIdNum === 1) {
          if (isEngineeringEnvironment) {
            const data = await MatterService.getProjectMatters();
            options = (data || []).map((item) => ({
              value: item.matterid,
              label: `${item.matterid}: ${item.mattername || ''}`,
            }));
          } else {
            const data = await MatterService.getMatters();
            options = (data || []).map((matter: Matter) => ({
              value: matter.matterid,
              label: `${matter.matterid}: ${matter.mattername || ''}`,
            }));
          }
        } else if (permishIdNum === 2 || permishIdNum === 3) {
          const data = await MatterService.getProjectMattersByEmployee(employeeId);
          options = (data || []).map((item) => ({
            value: item.matterid,
            label: `${item.matterid}: ${item.mattername || ''}`,
          }));
        }

        setMatters(options);
      } catch (error) {
        console.error('Error fetching matters:', error);
        setError(isEngineeringEnvironment ? 'Failed to load projects.' : 'Failed to load matters.');
      } finally {
        setLoadingMatters(false);
      }
    };

    fetchMatters();
  }, [currentEmployee, employeeId]);

  // Fetch employees
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const data: Employees[] = await EmployeeService.getEmployees();
        const options: SelectOption[] = data.map((employee) => ({
          value: employee.EmployeeID,
          label: `${employee.FirstName} ${employee.LastName}`,
        }));
        setEmployees(options);

        const currentEmp = data.find((emp) => emp.EmployeeID === employeeId);
        if (currentEmp) {
          setCurrentEmployee(currentEmp);
        }
      } catch (error) {
        console.error('Error fetching employees:', error);
        setError('Failed to load employees.');
      } finally {
        setLoadingEmployees(false);
      }
    };

    fetchEmployees();
  }, [employeeId]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      createdby: employeeId || '',
    }));
  }, [employeeId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleMatterChange = (selectedOption: SelectOption | null) => {
    setFormData({
      ...formData,
      matterid: selectedOption ? selectedOption.value : '',
    });
  };

  const handleTaskTypeChange = (selectedOption: SelectOption | null) => {
    setFormData({
      ...formData,
      task_type: selectedOption ? selectedOption.value : '',
    });
  };

  const handleAssignedToChange = (selectedOption: SelectOption | null) => {
    setFormData({
      ...formData,
      assignedto: selectedOption ? selectedOption.value : '',
    });
  };

  const handleStatusChange = (selectedOption: SelectOption | null) => {
    setFormData({
      ...formData,
      status: selectedOption ? selectedOption.value : 'Pending',
    });
  };

  const handlePriorityChange = (selectedOption: SelectOption | null) => {
    setFormData({
      ...formData,
      priority: selectedOption ? selectedOption.value : 'Medium',
    });
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      navigate('/tasks');
    }
  };

  const handleTaskCreation = async () => {
    setLoading(true);
    setError('');

    if (!employeeId) {
      setLoading(false);
      toastService.error('Unable to create task', {
        description: 'Unable to determine your account. Please refresh and try again.',
      });
      setError('Unable to determine your account. Please refresh and try again.');
      return;
    }

    if (!formData.task_title || !formData.matterid || !formData.assignedto) {
      setLoading(false);
      const missingFields = [];
      if (!formData.task_title) missingFields.push('Task Title');
      if (!formData.matterid) missingFields.push(matterOrProjectLabel);
      if (!formData.assignedto) missingFields.push('Assigned Leader');
      
      toastService.warning('Required fields missing', {
        description: `Please fill in: ${missingFields.join(', ')}`,
      });
      setError('Please fill in all required fields.');
      return;
    }

    try {
      const payload: AddTaskRequest = {
        matterid: formData.matterid,
        task_title: formData.task_title,
        description: formData.description ?? '',
        duedate: formData.duedate || null,
        assignedto: formData.assignedto,
        status: formData.status ?? 'Pending',
        createdby: employeeId,
        task_type: formData.task_type || 'Internal',
        priority: formData.priority ?? undefined,
      };
      const response: AddTaskResponse = await TaskService.addTask(payload);
      if (response.message.includes('error')) throw new Error(response.message);

      await queryClient.invalidateQueries({ queryKey: ['tasks', 'all'] });
      clearDraft();
      toastService.success('Action item created successfully', {
        description: `"${formData.task_title}" has been added.`,
      });
      handleClose();
    } catch (error) {
      console.error('Error adding task:', error);
      toastService.error('Failed to create task', {
        description: 'Please try again or contact support if the issue persists.',
      });
      setError('Failed to add the task. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getSelectedEmployee = () => {
    if (!formData.assignedto) return null;
    return employees.find((emp) => emp.value === formData.assignedto);
  };

  const selectedEmployee = getSelectedEmployee();

  const descriptionLength = formData.description?.length || 0;
  const maxDescriptionLength = 500;

  const matterOrProjectLabel = isEngineeringEnvironment ? 'Project' : 'Matter';
  const matterOrProjectPlaceholder = isEngineeringEnvironment
    ? 'Search and select project'
    : 'Search and select matter';
  const matterOrProjectInfo = isEngineeringEnvironment
    ? 'This links the action item to a specific project'
    : 'This links the action item to a specific matter';

  return (
    <div className="create-tasks-overlay">
      <div className="create-tasks-panel">
        {/* Header */}
        <div className="create-tasks-header">
          <div className="create-tasks-header-left">
            <div className="create-tasks-title-row">
              <div className="create-tasks-header-icon">
                <FontAwesomeIcon icon={faPlus} />
              </div>
              <div className="create-tasks-header-text">
                <h2 className="create-tasks-title">Create New Action Item</h2>
                <p className="create-tasks-subtitle">Add a new task to track and manage</p>
              </div>
            </div>
          </div>
          <button
            type="button"
            className="create-tasks-close"
            onClick={handleClose}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Draft restored banner */}
        {draftRestored && (
          <div className="create-tasks-draft-banner">
            <span>Draft restored from your previous session.</span>
            <div className="create-tasks-draft-banner-actions">
              <button
                type="button"
                className="create-tasks-draft-dismiss"
                onClick={dismissDraft}
              >
                Dismiss
              </button>
              <button
                type="button"
                className="create-tasks-draft-discard"
                onClick={() => {
                  clearDraft();
                  setFormData({ ...TASK_FORM_DEFAULTS, createdby: employeeId || '' });
                }}
              >
                Discard Draft
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="create-tasks-content">
          {/* Basic Information Section - Light Blue */}
          <div className="create-tasks-section create-tasks-section-blue">
            <div className="create-tasks-section-header">
              <div className="create-tasks-section-icon-wrapper create-tasks-icon-blue">
                <FontAwesomeIcon icon={faInfoCircle} />
              </div>
              <h3 className="create-tasks-section-title">Basic Information</h3>
            </div>
            <div className="create-tasks-section-content">
              <FormInput
                label="Action Item Title*"
                name="task_title"
                value={formData.task_title}
                onChange={handleInputChange}
                placeholder="e.g., Review contract amendments, Prepare client presentation"
                maxLength={255}
                required
              />
              <div className="create-tasks-select-wrapper">
                <FontAwesomeIcon icon={faSearch} className="create-tasks-select-icon" />
                <Select
                  options={matters}
                  isLoading={loadingMatters}
                  value={matters.find((matter) => matter.value === formData.matterid) || null}
                  onChange={handleMatterChange}
                  placeholder={matterOrProjectPlaceholder}
                  isClearable
                  className="create-tasks-select"
                  classNamePrefix="create-tasks-select"
                />
              </div>
              <div className="create-tasks-info-box">
                <FontAwesomeIcon icon={faInfoCircle} className="create-tasks-info-icon" />
                <span>{matterOrProjectInfo}</span>
              </div>
            </div>
          </div>

          {/* Assignment & Timing Section - Light Purple */}
          <div className="create-tasks-section create-tasks-section-purple">
            <div className="create-tasks-section-header">
              <div className="create-tasks-section-icon-wrapper create-tasks-icon-purple">
                <FontAwesomeIcon icon={faUser} />
              </div>
              <h3 className="create-tasks-section-title">Assignment & Timing</h3>
            </div>
            <div className="create-tasks-section-content">
              <div className="create-tasks-select-wrapper create-tasks-select-wrapper-no-icon">
                <Select
                  options={employees}
                  isLoading={loadingEmployees}
                  value={selectedEmployee || null}
                  onChange={handleAssignedToChange}
                  placeholder="Search and select leader"
                  className="create-tasks-select"
                  classNamePrefix="create-tasks-select"
                />
              </div>
              <div className="create-tasks-date-input-wrapper">
                <FormInput
                  label="Due Date*"
                  type="date"
                  name="duedate"
                  value={formData.duedate}
                  onChange={handleInputChange}
                  placeholder="mm/dd/yyyy"
                  required
                />
              </div>
            </div>
          </div>

          {/* Classification & Status Section - Light Green */}
          <div className="create-tasks-section create-tasks-section-green">
            <div className="create-tasks-section-header">
              <div className="create-tasks-section-icon-wrapper create-tasks-icon-green">
                <FontAwesomeIcon icon={faLeaf} />
              </div>
              <h3 className="create-tasks-section-title">Classification & Status</h3>
            </div>
            <div className="create-tasks-section-content">
              <div className="create-tasks-dropdowns-row">
                <div className="create-tasks-dropdown-wrapper">
                  <label className="create-tasks-dropdown-label">Scope*</label>
                  <Select
                    options={taskTypes}
                    value={taskTypes.find((t) => t.value === formData.task_type) || null}
                    onChange={handleTaskTypeChange}
                    placeholder="Select scope"
                    className="create-tasks-select"
                    classNamePrefix="create-tasks-select"
                  />
                  <div className="create-tasks-info-box-small stacked">
                    <strong>Internal:</strong> Team-facing tasks
                    <br />
                    <strong>External:</strong> Client-facing tasks
                  </div>
                </div>
                <div className="create-tasks-dropdown-wrapper">
                  <label className="create-tasks-dropdown-label">Priority*</label>
                  <Select
                    options={priorityOptions}
                    value={priorityOptions.find((p) => p.value === formData.priority) || null}
                    onChange={handlePriorityChange}
                    placeholder="Select priority"
                    className="create-tasks-select"
                    classNamePrefix="create-tasks-select"
                  />
                  <div className="create-tasks-info-box-small stacked">
                    <strong>Priority:</strong> Task urgency level
                  </div>
                </div>
                <div className="create-tasks-dropdown-wrapper">
                  <label className="create-tasks-dropdown-label">Initial Status*</label>
                  <Select
                    options={statusOptions}
                    value={statusOptions.find((opt) => opt.value === formData.status) || null}
                    onChange={handleStatusChange}
                    className="create-tasks-select"
                    classNamePrefix="create-tasks-select"
                  />
                  <div className="create-tasks-info-box-small stacked">
                    <strong>Initial Status:</strong> Starting state for this task
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Description Section - White */}
          <div className="create-tasks-section create-tasks-section-white">
            <div className="create-tasks-section-header">
              <h3 className="create-tasks-section-title">Description (Optional)</h3>
            </div>
            <div className="create-tasks-section-content">
              <div className="create-tasks-textarea-wrapper">
                <textarea
                  name="description"
                  value={formData.description || ''}
                  onChange={handleInputChange}
                  placeholder="Provide additional context, requirements, or notes about this action item..."
                  rows={6}
                  className="create-tasks-textarea"
                  maxLength={maxDescriptionLength}
                />
                <div className="create-tasks-char-counter">
                  {descriptionLength}/{maxDescriptionLength}
                </div>
              </div>
              <div className="create-tasks-info-box">
                <FontAwesomeIcon icon={faInfoCircle} className="create-tasks-info-icon" />
                <span>Add any relevant details to help team members understand the task</span>
              </div>
            </div>
          </div>

          {error && <div className="create-tasks-error">{error}</div>}
        </div>

        {/* Footer Actions */}
        <div className="create-tasks-footer">
          <div className="create-tasks-footer-info">
            <FontAwesomeIcon icon={faInfoCircle} className="create-tasks-footer-info-icon" />
            <span>All required fields must be completed</span>
          </div>
          <div className="create-tasks-footer-buttons">
            <button
              type="button"
              className="create-tasks-button create-tasks-button-cancel"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="create-tasks-button create-tasks-button-primary"
              onClick={handleTaskCreation}
              disabled={loading}
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  Creating...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCheckCircle} />
                  Create Action Item
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateTasksPage;
